<template>
  <modal :name="modalName" :width="640" :height="'auto'" :scrollable="true" :adaptive="true" @before-close="reset">
    <div class="imp">
      <div class="imp-header">
        <div class="imp-header__left">
          <div class="imp-header__icon"><fa-icon icon="upload" /></div>
          <div>
            <div class="imp-header__eyebrow">Importação de gatilho</div>
            <h3 class="imp-header__title">Importar a partir de um arquivo</h3>
          </div>
        </div>
        <button type="button" class="imp-close" @click="close"><fa-icon icon="times" /></button>
      </div>

      <div class="imp-body">
        <!-- Modelo -->
        <section class="imp-section">
          <div class="imp-section__head">
            <span class="imp-section__step">1</span>
            <div>
              <h4 class="imp-section__title">Baixe o arquivo modelo</h4>
              <p class="imp-section__hint">
                O modelo (.json) traz a estrutura esperada — campos, condições e ações — pronta para você preencher.
              </p>
            </div>
          </div>
          <div class="imp-model-card">
            <div class="imp-model-card__icon"><fa-icon icon="download" /></div>
            <div class="imp-model-card__body">
              <div class="imp-model-card__title">modelo-gatilho.json</div>
              <div class="imp-model-card__desc">Estrutura base com exemplos de evento, condição e ação.</div>
            </div>
            <button type="button" class="imp-btn imp-btn--ghost" @click="$emit('baixar-modelo')">
              <fa-icon icon="download" /> Baixar modelo
            </button>
          </div>
        </section>

        <!-- Upload -->
        <section class="imp-section">
          <div class="imp-section__head">
            <span class="imp-section__step">2</span>
            <div>
              <h4 class="imp-section__title">Envie o arquivo preenchido</h4>
              <p class="imp-section__hint">
                Arraste o arquivo ou clique na área abaixo. Apenas arquivos <strong>.json</strong> são aceitos.
              </p>
            </div>
          </div>

          <label
            class="imp-drop"
            :class="{ 'is-dragover': dragOver, 'has-file': !!file, 'has-error': !!erro }"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDrop"
          >
            <input
              ref="fileInput"
              type="file"
              accept="application/json,.json"
              class="imp-drop__input"
              @change="onFileChange"
            />
            <template v-if="!file">
              <div class="imp-drop__icon"><fa-icon icon="upload" /></div>
              <div class="imp-drop__title">Arraste o arquivo .json aqui</div>
              <div class="imp-drop__hint">ou clique para selecionar do computador</div>
            </template>
            <template v-else>
              <div class="imp-drop__icon imp-drop__icon--ok"><fa-icon icon="check-circle" /></div>
              <div class="imp-drop__title">{{ file.name }}</div>
              <div class="imp-drop__hint">{{ formatSize(file.size) }} — pronto para importar</div>
              <button type="button" class="imp-drop__remove" @click.prevent="limparArquivo">
                <fa-icon icon="times" /> Remover
              </button>
            </template>
          </label>

          <div v-if="erro" class="imp-error">
            <fa-icon icon="exclamation-triangle" /> {{ erro }}
          </div>
        </section>
      </div>

      <div class="imp-footer">
        <button type="button" class="imp-btn imp-btn--ghost" @click="close">Cancelar</button>
        <button type="button" class="imp-btn imp-btn--primary" :disabled="!file" @click="confirmar">
          <fa-icon icon="upload" /> Importar
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
export default {
  name: 'AcaoObjetoImportarModal',
  props: { modalName: { type: String, default: 'acaoObjetoImportar' } },
  data() {
    return {
      file: null,
      dragOver: false,
      erro: '',
    };
  },
  methods: {
    onFileChange(e) {
      const f = e.target.files && e.target.files[0];
      this.setFile(f);
    },
    onDrop(e) {
      this.dragOver = false;
      const f = e.dataTransfer.files && e.dataTransfer.files[0];
      this.setFile(f);
    },
    setFile(f) {
      this.erro = '';
      if (!f) { this.file = null; return; }
      const isJson = f.type === 'application/json' || /\.json$/i.test(f.name);
      if (!isJson) {
        this.file = null;
        this.erro = 'Selecione um arquivo no formato .json.';
        return;
      }
      this.file = f;
    },
    limparArquivo() {
      this.file = null;
      this.erro = '';
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    confirmar() {
      if (!this.file) return;
      const reader = new FileReader();
      reader.onload = ev => {
        try {
          const data = JSON.parse(ev.target.result);
          this.$emit('importar', data);
          this.close();
        } catch (err) {
          this.erro = 'JSON inválido: ' + err.message;
        }
      };
      reader.onerror = () => { this.erro = 'Falha ao ler o arquivo.'; };
      reader.readAsText(this.file);
    },
    formatSize(bytes) {
      if (!bytes) return '0 B';
      const u = ['B', 'KB', 'MB'];
      let i = 0; let v = bytes;
      while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
      return `${v.toFixed(v < 10 && i ? 1 : 0)} ${u[i]}`;
    },
    reset() {
      this.file = null;
      this.dragOver = false;
      this.erro = '';
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    close() { this.$modal.hide(this.modalName); },
  },
};
</script>

<style scoped>
.imp { background: #fff; }
.imp-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 22px;
  background: linear-gradient(135deg, #f9fafb 0%, #f2f2f8 100%);
  border-bottom: 1px solid #e4e7eb;
}
.imp-header__left { display: flex; align-items: center; gap: 14px; }
.imp-header__icon {
  width: 42px; height: 42px; border-radius: 10px;
  background: linear-gradient(135deg, #3a3e81, #5b60a6);
  color: #fff; display: flex; align-items: center; justify-content: center; font-size: 16px;
}
.imp-header__eyebrow { font-size: 11px; font-weight: 600; color: #3a3e81; text-transform: uppercase; letter-spacing: 0.5px; }
.imp-header__title { margin: 2px 0 0; font-size: 18px; font-weight: 700; color: #1d2939; }
.imp-close { width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e4e7eb; background: #fff; color: #667085; cursor: pointer; }
.imp-close:hover { background: #d9534f; color: #fff; border-color: #d9534f; }

.imp-body { padding: 20px 22px; display: flex; flex-direction: column; gap: 22px; }

.imp-section__head { display: flex; gap: 12px; align-items: flex-start; margin-bottom: 12px; }
.imp-section__step {
  width: 26px; height: 26px; border-radius: 50%;
  background: #3a3e81; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; flex-shrink: 0;
}
.imp-section__title { margin: 0; font-size: 14px; font-weight: 700; color: #1d2939; }
.imp-section__hint { margin: 2px 0 0; font-size: 12px; color: #667085; line-height: 1.5; }

.imp-model-card {
  display: flex; align-items: center; gap: 14px;
  padding: 14px; border: 1px solid #e4e7eb; border-radius: 10px;
  background: #f9fafb;
}
.imp-model-card__icon {
  width: 38px; height: 38px; border-radius: 8px;
  background: #f2f2f8; color: #3a3e81;
  display: flex; align-items: center; justify-content: center; font-size: 14px; flex-shrink: 0;
}
.imp-model-card__body { flex: 1; min-width: 0; }
.imp-model-card__title { font-size: 13px; font-weight: 700; color: #1d2939; }
.imp-model-card__desc { font-size: 12px; color: #667085; margin-top: 2px; }

.imp-drop {
  position: relative;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px;
  padding: 28px 18px;
  border: 2px dashed #dddce9;
  border-radius: 12px;
  background: #fafbfc;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;
}
.imp-drop:hover { border-color: #3a3e81; background: #f7f7fc; }
.imp-drop.is-dragover { border-color: #3a3e81; background: #f2f2f8; }
.imp-drop.has-file { border-style: solid; border-color: #b9e7be; background: #f5fbf6; }
.imp-drop.has-error { border-color: #e89189; background: #fdf6f5; }
.imp-drop__input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.imp-drop__icon {
  width: 44px; height: 44px; border-radius: 50%;
  background: #f2f2f8; color: #3a3e81;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 18px; margin-bottom: 4px;
}
.imp-drop__icon--ok { background: #ecfaee; color: #2e7d32; }
.imp-drop__title { font-size: 14px; font-weight: 700; color: #1d2939; }
.imp-drop__hint { font-size: 12px; color: #667085; }
.imp-drop__remove {
  margin-top: 8px;
  background: #fff; border: 1px solid #fbd7d6; color: #d9534f;
  border-radius: 6px; padding: 6px 12px; font-size: 11px; font-weight: 600;
  cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  position: relative; z-index: 1;
}
.imp-drop__remove:hover { background: #d9534f; color: #fff; border-color: #d9534f; }

.imp-error {
  margin-top: 10px;
  display: flex; align-items: center; gap: 8px;
  font-size: 12px; color: #b3261e;
  background: #fdecea; border: 1px solid #e89189;
  border-radius: 8px; padding: 8px 12px;
}

.imp-footer {
  padding: 14px 22px; border-top: 1px solid #e4e7eb;
  display: flex; justify-content: flex-end; gap: 8px;
}
.imp-btn {
  padding: 9px 18px; border-radius: 8px;
  font-size: 13px; font-weight: 600; cursor: pointer;
  display: inline-flex; align-items: center; gap: 8px;
  transition: all 0.15s ease;
  border: 1px solid transparent;
}
.imp-btn--ghost { background: #fff; color: #3a3e81; border-color: #dddce9; }
.imp-btn--ghost:hover { background: #f2f2f8; }
.imp-btn--primary { background: #3a3e81; color: #fff; border-color: #3a3e81; box-shadow: 0 2px 6px rgba(58, 62, 129, 0.2); }
.imp-btn--primary:hover:not(:disabled) { background: #2f326b; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(58, 62, 129, 0.3); }
.imp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
</content>
</invoke>